import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { getPages, type Paged } from './pagedList';
import { useAuth } from '@/lib/auth';
import { money } from '@/lib/utils';
import { Badge, Field, Input } from '@/components/ui/primitives';
import { useDebounced } from './LabBenchDebounce';

interface LabTest {
  id: string;
  code: string;
  name: string;
  departmentGroup: string;
  specimenType: string;
  basePriceMinor: number;
}

interface LabPanel {
  id: string;
  code: string;
  name: string;
  basePriceMinor: number;
  tests: { test: { id: string; code: string; name: string } }[];
}

export interface LabBenchOrderable {
  kind: 'test' | 'panel';
  id: string;
  code: string;
  name: string;
  detail: string;
  priceMinor: number;
}

export function splitOrderables(chosen: LabBenchOrderable[]): { testIds: string[]; panelIds: string[] } {
  return {
    testIds: chosen.filter((c) => c.kind === 'test').map((c) => c.id),
    panelIds: chosen.filter((c) => c.kind === 'panel').map((c) => c.id),
  };
}

export function LabBenchTestPicker({
  chosen,
  onChange,
  inputId,
}: {
  chosen: LabBenchOrderable[];
  onChange: (next: LabBenchOrderable[]) => void;
  inputId: string;
}) {
  const { can } = useAuth();
  const [testQuery, setTestQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const searched = useDebounced(testQuery.trim(), 300);

  const tests = useQuery({
    queryKey: ['lab-tests-search', searched],
    queryFn: async () => (await get<Paged<LabTest>>(`/lab/tests${qs({ q: searched || undefined, limit: 20 })}`)).data,
    placeholderData: keepPreviousData,
  });
  const panels = useQuery({
    queryKey: ['lab-panels'],
    queryFn: () => getPages<LabPanel>('/lab/panels', { limit: 200 }),
  });

  const needle = testQuery.trim().toLowerCase();
  const options: LabBenchOrderable[] = [
    ...(panels.data ?? [])
      .filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.code.toLowerCase().includes(needle))
      .map<LabBenchOrderable>((p) => ({
        kind: 'panel',
        id: p.id,
        code: p.code,
        name: p.name,
        detail: `Panel · ${p.tests.length} test${p.tests.length === 1 ? '' : 's'}`,
        priceMinor: p.basePriceMinor,
      })),
    ...(tests.data ?? []).map<LabBenchOrderable>((t) => ({
      kind: 'test',
      id: t.id,
      code: t.code,
      name: t.name,
      detail: t.specimenType,
      priceMinor: t.basePriceMinor,
    })),
  ].filter((o) => !chosen.some((c) => c.kind === o.kind && c.id === o.id));
  const catalogueEmpty = tests.isSuccess && panels.isSuccess && !needle && tests.data.length === 0 && panels.data.length === 0;
  const total = chosen.reduce((sum, c) => sum + c.priceMinor, 0);

  return (
    <>
      <Field label={tr('lab', 'tests')} htmlFor={inputId} required hint={tr('lab', 'search_by_name_or_code_then_click_a_test_or')}>
        <Input
          id={inputId}
          value={testQuery}
          onChange={(e) => setTestQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          placeholder={tr('lab', 'e_g_cbc_haemoglobin_tsh')}
          autoComplete="off"
        />
        {(focused || needle) && (
          <div className="mt-1 max-h-48 overflow-y-auto rounded-md border">
            {options.map((o) => (
              <button
                key={`${o.kind}:${o.id}`}
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-start text-sm hover:bg-accent/50"
                onClick={() => {
                  onChange([...chosen, o]);
                  setTestQuery('');
                }}
              >
                <span>
                  {o.name} <span className="text-xs text-muted-foreground">({o.code})</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {o.detail}
                  {o.priceMinor > 0 && ` · ${money(o.priceMinor)}`}
                </span>
              </button>
            ))}
            {options.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {catalogueEmpty
                  ? tr('lab', 'your_test_catalogue_is_empty')
                  : needle
                    ? tr('lab', 'nothing_in_the_catalogue_matches_x', { value: testQuery.trim() })
                    : tr('lab', 'everything_matching_is_already_on_the_order')}
                {catalogueEmpty && can('lab.master.manage') && (
                  <>
                    {' '}
                    <Link to="/lab/catalogue" className="font-medium text-primary underline-offset-4 hover:underline">
                      {tr('lab', 'add_tests_and_panels')}
                    </Link>{' '}
                    {tr('lab', 'first')}
                  </>
                )}
              </p>
            )}
          </div>
        )}
      </Field>

      {chosen.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chosen.map((c) => (
            <Badge key={`${c.kind}:${c.id}`} variant="secondary" className="gap-1">
              {c.kind === 'panel' && <span className="text-[10px] uppercase opacity-70">{tr('lab', 'panel')}</span>}
              {c.name}
              <button
                type="button"
                aria-label={tr('lab', 'remove_x', { name: c.name })}
                onClick={() => onChange(chosen.filter((x) => !(x.kind === c.kind && x.id === c.id)))}
                className="ms-0.5 opacity-70 hover:opacity-100"
              >
                ×
              </button>
            </Badge>
          ))}
          {total > 0 && <span className="ms-auto text-sm text-muted-foreground">{tr('lab', 'total')} {money(total)}</span>}
        </div>
      )}
    </>
  );
}
